import axios from 'axios';
import { CognitoUserPool } from 'amazon-cognito-identity-js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
const TOKEN_REFRESH_BUFFER_SECONDS = 60;

// Track redirect to prevent infinite loops
let redirectInProgress = false;
let last401Timestamp = 0;
let tokenRefreshPromise = null;
const REDIRECT_DEBOUNCE_TIME = 5000; // 5 seconds

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get the current Cognito ID token (for use outside React components)
const poolData = {
  UserPoolId: 'ap-south-1_b5xxiF0wW',
  ClientId: 'oortpn6ip944a1rch7c9o19q7'
};
const userPool = new CognitoUserPool(poolData);
const getCognitoSession = () => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err || !session || !session.isValid()) {
          reject(new Error('No valid Cognito session'));
        } else {
          resolve({ cognitoUser, session });
        }
      });
    } else {
      reject(new Error('No Cognito user'));
    }
  });
};

const tokenExpiresSoon = (token) => {
  if (!token) return true;

  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = JSON.parse(window.atob(padded));
    const now = Math.floor(Date.now() / 1000);
    return !decoded.exp || decoded.exp - now <= TOKEN_REFRESH_BUFFER_SECONDS;
  } catch (error) {
    return true;
  }
};

const getMobileStoredTokens = () => {
  try {
    return JSON.parse(localStorage.getItem('mobile_auth_tokens') || 'null');
  } catch (error) {
    return null;
  }
};

const storeTokens = (tokens) => {
  if (!tokens) return;

  const idToken = tokens.idToken || tokens.IdToken;
  const accessToken = tokens.accessToken || tokens.AccessToken;
  const refreshToken = tokens.refreshToken || tokens.RefreshToken || localStorage.getItem('refreshToken');
  const deviceKey = tokens.deviceKey || tokens.DeviceKey || getMobileStoredTokens()?.deviceKey;
  const expiresIn = Number(tokens.expiresIn || tokens.ExpiresIn || 3600);
  const expiresAt = Date.now() + expiresIn * 1000;

  if (idToken) localStorage.setItem('idToken', idToken);
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('tokenExpiry', expiresAt.toString());

  // Keep the token format used by MobileAuthService synchronized.
  localStorage.setItem('mobile_auth_tokens', JSON.stringify({
    idToken,
    accessToken,
    refreshToken,
    deviceKey,
    expiresIn,
    expiresAt,
    timestamp: Date.now()
  }));
};

const refreshCognitoSession = async () => {
  const { cognitoUser, session } = await getCognitoSession();
  const refreshToken = session.getRefreshToken();

  if (!refreshToken) {
    throw new Error('No Cognito refresh token');
  }

  const refreshedSession = await new Promise((resolve, reject) => {
    cognitoUser.refreshSession(refreshToken, (error, result) => {
      if (error || !result) {
        reject(error || new Error('Cognito token refresh failed'));
      } else {
        resolve(result);
      }
    });
  });

  const tokens = {
    idToken: refreshedSession.getIdToken().getJwtToken(),
    accessToken: refreshedSession.getAccessToken().getJwtToken(),
    refreshToken: refreshedSession.getRefreshToken().getToken(),
    expiresIn: Math.max(
      refreshedSession.getIdToken().getExpiration() - Math.floor(Date.now() / 1000),
      0
    )
  };
  storeTokens(tokens);
  return tokens.idToken;
};

const refreshStoredSession = async () => {
  // Older mobile sessions stored tokens only in the MobileAuthService bundle.
  // Keep supporting those sessions so an app update does not sign users out.
  const refreshToken = localStorage.getItem('refreshToken') || getMobileStoredTokens()?.refreshToken;
  const deviceKey = getMobileStoredTokens()?.deviceKey;
  if (!refreshToken) {
    throw new Error('No stored refresh token');
  }

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const response = await fetch(`${API_BASE_URL}/msg91/refresh-tokens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      refreshToken,
      deviceKey,
      phoneNumber: storedUser.phoneNumber || storedUser.username
    })
  });
  const result = await response.json().catch(() => ({}));

  if (!response.ok || !result.tokens) {
    throw new Error(result.message || result.error || 'Stored token refresh failed');
  }

  storeTokens(result.tokens);
  return result.tokens.idToken || result.tokens.IdToken ||
    result.tokens.accessToken || result.tokens.AccessToken;
};

const refreshAuthToken = () => {
  if (!tokenRefreshPromise) {
    tokenRefreshPromise = (async () => {
      try {
        return await refreshCognitoSession();
      } catch (cognitoError) {
        return refreshStoredSession();
      }
    })().finally(() => {
      tokenRefreshPromise = null;
    });
  }

  return tokenRefreshPromise;
};

const getValidAuthToken = async () => {
  const mobileTokens = getMobileStoredTokens();
  const storedToken = localStorage.getItem('idToken') || localStorage.getItem('accessToken') ||
    mobileTokens?.idToken || mobileTokens?.accessToken;
  if (storedToken && !tokenExpiresSoon(storedToken)) {
    return storedToken;
  }

  try {
    const { session } = await getCognitoSession();
    const idToken = session.getIdToken().getJwtToken();
    if (!tokenExpiresSoon(idToken)) {
      storeTokens({
        idToken,
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresIn: Math.max(
          session.getIdToken().getExpiration() - Math.floor(Date.now() / 1000),
          0
        )
      });
      return idToken;
    }
  } catch (error) {
    // The stored-token refresh flow below also supports MSG91 authentication.
  }

  return refreshAuthToken();
};

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await getValidAuthToken();
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Public endpoints can continue without an Authorization header.
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // A token can expire between the request check and API authorization.
      // Refresh and retry the original request once before ending the session.
      if (originalRequest && !originalRequest._authRetry) {
        originalRequest._authRetry = true;
        try {
          const token = await refreshAuthToken();
          if (token) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Continue to the shared session-expiry handling below.
        }
      }

      // Prevent infinite loop - check if we're already handling a 401 or on login page
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === '/login' || currentPath === '/mobile-login' || currentPath === '/';
      const now = Date.now();
      
      // Debounce multiple 401s within 5 seconds
      if (redirectInProgress || (now - last401Timestamp < REDIRECT_DEBOUNCE_TIME)) {
        console.warn('[API] 401 redirect already in progress or too recent, skipping');
        return Promise.reject(error);
      }
      
      // If already on login page, don't redirect - just clear tokens
      if (isOnLoginPage) {
        console.warn('[API] 401 on login page, clearing tokens without redirect');
        clearAuthenticationData();
        return Promise.reject(error);
      }
      
      // Mark redirect in progress and update timestamp
      redirectInProgress = true;
      last401Timestamp = now;
      
      try {
        // Clear all authentication data
        await clearAuthenticationData();
        
        console.log('[API] 401 detected, redirecting to login');
        window.dispatchEvent(new CustomEvent('auth:session-expired'));

        // AuthContext normally handles navigation. Keep a full-page fallback
        // for requests made before React finishes mounting.
        setTimeout(() => {
          redirectInProgress = false;
          if (window.location.pathname !== '/login') {
            window.location.replace('/login');
          }
        }, 100);
      } catch (signOutError) {
        console.error('[API] Error during 401 cleanup:', signOutError);
        redirectInProgress = false;
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to clear all authentication data
const clearAuthenticationData = async () => {
  try {
    // Clear localStorage tokens
    const tokenKeys = [
      'idToken',
      'accessToken',
      'refreshToken',
      'tokenExpiry',
      'user',
      'mobile_auth_tokens',
      'mobile_user_data'
    ];
    tokenKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove stored authentication value ${key}:`, e);
      }
    });
    
    // Clear Cognito session
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        await new Promise((resolve) => {
          cognitoUser.signOut();
          resolve();
        });
      }
    } catch (cognitoError) {
      console.warn('Failed to clear Cognito session:', cognitoError);
    }
    
    console.log('[API] Authentication data cleared successfully');
  } catch (error) {
    console.error('[API] Error clearing authentication data:', error);
  }
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getUsers: (buildingId, params) => api.get(`/users/building/${buildingId}`, { params }),
  getAssociations: (userId) => api.get(`/users/${userId}/associations`),
  addAssociations: (userId, data) => api.post(`/users/${userId}/associations`, data),
  removeAssociations: (userId, data) => api.delete(`/users/${userId}/associations`, { data }),
};

// Buildings API
export const buildingsAPI = {
  getAll: () => api.get('/buildings'),
  getById: (id) => api.get(`/buildings/${id}`),
  create: (data) => api.post('/buildings', data),
  update: (id, data) => api.put(`/buildings/${id}`, data),
  delete: (id) => api.delete(`/buildings/${id}`),
};

// Flats API
export const flatsAPI = {
  getAll: () => api.get('/flats'),
  getById: (id) => api.get(`/flats/${id}`),
  create: (data) => api.post('/flats', data),
  update: (id, data) => api.put(`/flats/${id}`, data),
  delete: (id) => api.delete(`/flats/${id}`),
  getByBuilding: (buildingId) => api.get(`/flats/building/${buildingId}`),
  getDetails: (flatId, flatNumber) => api.get(`/flats/details/${flatId}`, { params: { flatNumber } }),
  getHistory: (flatId) => api.get(`/flats/history`, { params: { flatId } }),
  // QR Code validation for vehicle entry
  validateQRCode: (vehicleNumber, buildingId = null) => {
    const endpoint = buildingId 
      ? `/flats/building/${buildingId}` 
      : `/flats/building/DUMMY_BUILDING`;
    return api.get(endpoint, { params: { qrCode: vehicleNumber } });
  },
  // Vehicle entry logs
  getVehicleEntryLogs: (params = {}) => {
    return api.get('/flats/vehicle-entry-logs', { params });
  },
  // Create vehicle entry log
  createVehicleEntryLog: (data) => {
    return api.post('/flats/vehicle-entry-logs', data);
  },
};

// Payments API
export const paymentsAPI = {
  getAll: () => api.get('/payments'),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  pay: (paymentId) => api.post(`/payments/${paymentId}/pay`),
  // New retrieval methods based on access patterns
  getByBuilding: (buildingId) => api.get('/payments', { params: { buildingId } }),
  getByBuildingAndFlat: (buildingId, flatId) => api.get('/payments', { params: { buildingId, flatId } }),
  getByBuildingYearMonth: (buildingId, year, month) => api.get('/payments', { params: { buildingId, year, month } }),
  getByBuildingFlatYearMonth: (buildingId, flatId, year, month) => api.get('/payments', { params: { buildingId, flatId, year, month } }),
  getByFlat: (flatId) => api.get(`/payments/flat/${flatId}`),
  
  // CCAvenue payment methods
  initiateCCAvPayment: (data) => api.post('/ccavenue/initiate', data),
  payWithCCAvenue: (data) => api.put(`/payments/${data.paymentId}`, data),
  
  // Razorpay methods
  createRazorpayOrder: (data) => api.post('/razorpay-order', data),
  payWithRazorpay: (data) => api.put(`/payments/${data.paymentId}`, data),
  
  // Building summary method
  getBuildingSummary: (buildingId) => api.get('/payments', { params: { buildingId, summary: 'true' } }),
};

// Building admins use a buildingId; super admins omit it for system-wide upload.
export const reconciliationsAPI = {
  list: (params = {}) => api.get('/reconciliations', { params }),
  // Step 1: ask backend for a presigned PUT URL.
  getUploadUrl: (fileName, buildingId) => api.post('/reconciliations/upload', {
    fileName,
    ...(buildingId ? { buildingId } : {})
  }),
  // Step 2: PUT the file directly to the presigned URL (bypass axios api instance).
  uploadToS3: async (presigned, file) => {
    const contentType = presigned.contentType || (file.name?.toLowerCase().endsWith('.xls')
      ? 'application/vnd.ms-excel'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    const res = await fetch(presigned.url, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType
      },
      body: file
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
    return presigned;
  },
  reprocess: (key, buildingId) => api.post('/reconciliations/reprocess', { key, buildingId }),
  overridePaymentStatus: (data) => api.post('/reconciliations/override', data),
};

// Complaints API
export const complaintsAPI = {
  getAll: () => api.get('/complaints'),
  getById: (id) => api.get(`/complaints/${id}`),
  create: (data) => api.post('/complaints', data),
  update: (id, data) => api.put(`/complaints/${id}`, data),
  delete: (id) => api.delete(`/complaints/${id}`),
  // Accepts optional status param
  getByBuilding: (buildingId, status) => {
    let url = `/complaints/building/${buildingId}`;
    if (status) {
      url += `?status=${encodeURIComponent(status)}`;
    }
    return api.get(url);
  },
  getByFlat: (flatId) => api.get(`/complaints/flat/${flatId}`),
  getAssignedToMe: (params = {}) => api.get('/complaints', { params: { assignedTo: 'me', ...params } })
};

// Maintenance Staff API
export const maintenanceStaffAPI = {
  getAll: (buildingId) => api.get('/maintenance-staff', { params: { buildingId } }),
  getById: (id, buildingId) => api.get(`/maintenance-staff/${id}`, { params: { buildingId } }),
  create: (data) => api.post('/maintenance-staff', data),
  update: (id, data, buildingId) => api.put(`/maintenance-staff/${id}`, data, { params: { buildingId } }),
  delete: (id, buildingId) => api.delete(`/maintenance-staff/${id}`, { params: { buildingId } }),
};

// Staff attendance API. The backend derives and validates building/staff identity
// from the authenticated user; buildingId is only a compatibility hint for admins.
export const staffAttendanceAPI = {
  getSettings: (buildingId) => api.get('/staff-attendance/settings', { params: buildingId ? { buildingId } : undefined }),
  updateSettings: (data) => api.put('/staff-attendance/settings', data),
  getToday: () => api.get('/staff-attendance/today'),
  checkIn: (location) => api.post('/staff-attendance/check-in', { location }),
  checkOut: (location, attendanceDate) => api.post('/staff-attendance/check-out', { location, attendanceDate }),
  getMyHistory: (params = {}) => api.get('/staff-attendance/my-history', { params }),
  requestCorrection: (data) => api.post('/staff-attendance/correction-request', data),
  getBuildingAttendance: (params = {}) => api.get('/staff-attendance/building', { params }),
  createAttendance: (data) => api.post('/staff-attendance/admin-record', data),
  getSummary: (params = {}) => api.get('/staff-attendance/summary', { params }),
  updateAttendance: (attendanceId, data) => api.put(`/staff-attendance/${encodeURIComponent(attendanceId)}`, data),
  approveCorrection: (attendanceId, data = {}) => api.post(`/staff-attendance/${encodeURIComponent(attendanceId)}/approve-correction`, data),
  rejectCorrection: (attendanceId, data) => api.post(`/staff-attendance/${encodeURIComponent(attendanceId)}/reject-correction`, data),
  exportMonthly: (month, shiftId) => api.get('/staff-attendance/export', { params: { month, shiftId: shiftId || undefined }, responseType: 'blob' }),
  getLocations: (buildingId) => api.get('/staff-attendance/locations', { params: buildingId ? { buildingId } : undefined }),
  createLocation: (data) => api.post('/staff-attendance/locations', data),
  updateLocation: (locationId, data) => api.put(`/staff-attendance/locations/${encodeURIComponent(locationId)}`, data),
  rotateLocationQr: (locationId) => api.post(`/staff-attendance/locations/${encodeURIComponent(locationId)}/rotate-qr`),
  getRoundToday: () => api.get('/staff-attendance/rounds/today'),
  scanCheckpoint: (data) => api.post('/staff-attendance/rounds/scan', data),
  getRoundReport: (params = {}) => api.get('/staff-attendance/rounds/report', { params }),
};

export const marketplaceAPI = {
  list: (domain, params = {}) => api.get(`/${domain}/listings`, { params }),
  myListings: (domain, params = {}) => api.get(`/${domain}/my-listings`, { params }),
  create: (domain, data, buildingId) => api.post(`/${domain}/listings`, { ...data, buildingId }),
  uploadPhoto: async (domain, file, buildingId) => {
    const response = await api.post(`/${domain}/media-upload`, { contentType: file.type, fileName: file.name, buildingId });
    const form = new FormData();
    Object.entries(response.data.fields || {}).forEach(([key, value]) => form.append(key, value));
    form.append('file', file);
    let upload;
    try {
      upload = await fetch(response.data.uploadUrl, { method: 'POST', body: form });
    } catch (error) {
      throw new Error('Photo upload could not connect to storage. Please check your connection and try again.');
    }
    if (!upload.ok) throw new Error(`Photo upload failed: ${upload.status}`);
    return response.data;
  },
  update: (domain, id, data, buildingId) => api.put(`/${domain}/listings/${encodeURIComponent(id)}`, { ...data, buildingId }),
  removeListing: (domain, id, buildingId) => api.delete(`/${domain}/listings/${encodeURIComponent(id)}`, { params: { buildingId } }),
  publish: (domain, id, buildingId) => api.post(`/${domain}/listings/${encodeURIComponent(id)}/publish`, { buildingId }),
  action: (domain, id, action, method = 'post', data = {}, buildingId) => api[method](`/${domain}/listings/${encodeURIComponent(id)}/${action}`, { ...data, buildingId }),
  enquire: (domain, id, message, buildingId) => api.post(`/${domain}/listings/${encodeURIComponent(id)}/enquiries`, { message, buildingId }),
  receivedEnquiries: (domain, params = {}) => api.get(`/${domain}/received-enquiries`, { params }),
  favorite: (domain, id, buildingId) => api.post(`/${domain}/listings/${encodeURIComponent(id)}/favorite`, { buildingId }),
  removeFavorite: (domain, id, buildingId) => api.delete(`/${domain}/listings/${encodeURIComponent(id)}/favorite`, { params: { buildingId } }),
  favorites: (domain, params = {}) => api.get(`/${domain}/favorites`, { params }),
  report: (domain, id, reason, buildingId) => api.post(`/${domain}/listings/${encodeURIComponent(id)}/report`, { reason, buildingId }),
  reports: (domain, params = {}) => api.get(`/${domain}/admin/reports`, { params }),
  pending: (params = {}) => api.get('/marketplace/admin/pending-verification', { params }),
  moderate: (id, data, buildingId) => api.put(`/marketplace/admin/listings/${encodeURIComponent(id)}/moderate`, { ...data, buildingId }),
  getConfig: (buildingId) => api.get('/marketplace/config', { params: buildingId ? { buildingId } : undefined }),
  updateConfig: (data, buildingId) => api.put('/marketplace/config', { ...data, buildingId }),
  commissions: (params = {}) => api.get('/tolet/commissions', { params }),
  collectCommission: (id, data, buildingId) => api.post(`/tolet/commissions/${encodeURIComponent(id)}/collections`, { ...data, buildingId }),
  waiveCommission: (id, reason, buildingId) => api.post(`/tolet/commissions/${encodeURIComponent(id)}/waive`, { reason, buildingId })
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getById: (id) => api.get(`/notifications/${id}`),
  create: (data) => api.post('/notifications', data),
  update: (id, data) => api.put(`/notifications/${id}`, data),
  delete: (id) => api.delete(`/notifications/${id}`),
  
  // Mass notification endpoints
  sendMassNotification: (data) => api.post('/notifications/mass', data),
  sendAnnouncementToAdmins: (data) => api.post('/notifications/announcements', data),
  getTemplates: () => api.get('/notifications/templates'),
  
  // User-specific notifications
  getUserNotifications: (userId, days, buildingId) => api.get(`/notifications/user/${userId}`, {
    params: { ...(days ? { days } : {}), ...(buildingId ? { buildingId } : {}) }
  }),
  // Use the canonical notification update route. The Lambda marks the item as
  // read on PUT, and this route is part of every API Gateway deployment.
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}`, { isRead: true }),
  registerDevice: (data) => api.post('/notifications/devices', data),
  unregisterDevice: (deviceId) => api.delete(`/notifications/devices/${encodeURIComponent(deviceId)}`),
};

// Visitor Management API
export const visitorsAPI = {
  // Get all visitors for a building
  getByBuilding: (buildingId) => api.get(`/visitors/building/${buildingId}`),
  // Get all visitors for a flat
  getByFlat: (flatId) => api.get(`/visitors/flat/${flatId}`),
  // Create a new visitor request
  create: (data) => api.post('/visitors', data),
  // Update a visitor request (status or details)
  update: (buildingId, visitorId, data) =>
    api.put(`/visitors/building/${buildingId}/visitor/${visitorId}`, data),
  getByDate: (dateStr) => api.get(`/visitors?date=${dateStr}`),
  getByBuildingAndDate: (buildingId, dateStr) => api.get(`/visitors/building/${buildingId}`, { params: { date: dateStr } }), // <-- Add this line
  getParkingSlots: (buildingId) => api.get('/visitors', { params: { parkingBuildingId: buildingId } }),
  createParkingSlot: (data) => api.post('/visitors', { ...data, action: 'CREATE_PARKING_SLOT' }),
  deleteParkingSlot: (buildingId, slotId) => api.post('/visitors', { action: 'DELETE_PARKING_SLOT', buildingId, slotId }),
};

// Facilities API
export const facilitiesAPI = {
  getAll: (buildingId) => api.get('/facilities', { params: { buildingId } }),
  create: (data) => api.post('/facilities', data),
  update: (data) => api.put('/facilities', data),
};

// Facility Bookings API
export const facilityBookingsAPI = {
  create: (data) => api.post('/facility-bookings', data),
  update: (data) => api.put('/facility-bookings', data),
  delete: (bookingId) => api.delete('/facility-bookings', { params: { bookingId } }),
  getByUser: (userId) => api.get('/facility-bookings', { params: { userId } }),
  getByBuilding: (buildingId) => api.get('/facility-bookings', { params: { buildingId } }),
  getByDateRange: (buildingId, startDate, endDate) => api.get('/facility-bookings', { params: { buildingId, startDate, endDate } }),
  getByFacilityAndDate: (facilityId, buildingId, year, month) => api.get('/facility-bookings', { params: { facilityId, buildingId, year, month } }),
  getByFacilityDate: (facilityId, buildingId, date) => api.get('/facility-bookings', { params: { facilityId, buildingId, date } }),
  getByBookingId: (bookingId) => api.get('/facility-bookings', { params: { bookingId } }),
};

// Building Expenses API
export const expensesAPI = {
  // Get all expenses with optional filters
  getAll: (buildingId, filters = {}) => api.get('/expenses', { params: { buildingId, ...filters } }),
  
  // Get specific expense by ID
  getById: (expenseId, buildingId) => api.get(`/expenses/${expenseId}`, { params: { buildingId } }),
  
  // Create new expense
  create: (buildingId, data) => api.post('/expenses', data, { params: { buildingId } }),
  
  // Update existing expense
  update: (expenseId, buildingId, data) => api.put(`/expenses/${expenseId}`, data, { params: { buildingId } }),
  
  // Delete expense (soft delete)
  delete: (expenseId, buildingId) => api.delete(`/expenses/${expenseId}`, { params: { buildingId } }),
  
  // Approve or reject expense
  approve: (expenseId, buildingId, approvalData) => 
    api.post(`/expenses/${expenseId}/approve`, approvalData, { params: { buildingId } }),
  
  // Get monthly summary
  getMonthlySummary: (buildingId, month) => api.get(`/expenses/summary/${buildingId}/${month}`),
  
  // Get expense categories and metadata
  getCategories: () => api.get('/expenses/categories'),
  
  // Upload receipt file
  uploadReceipt: (buildingId, expenseId, formData) => 
    api.post('/expenses/receipts/upload', formData, {
      params: { buildingId, expenseId },
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Get expenses by category
  getByCategory: (buildingId, category, filters = {}) => 
    api.get('/expenses', { params: { buildingId, category, ...filters } }),
  
  // Get expenses by month
  getByMonth: (buildingId, month, filters = {}) => 
    api.get('/expenses', { params: { buildingId, month, ...filters } }),
  
  // Get expenses by vendor
  getByVendor: (buildingId, vendorId, filters = {}) => 
    api.get('/expenses', { params: { buildingId, vendorId, ...filters } }),
  
  // Get expenses by logged user
  getByLoggedUser: (buildingId, loggedBy, filters = {}) => 
    api.get('/expenses', { params: { buildingId, loggedBy, ...filters } }),
  
  // Get expenses by approval status
  getByApprovalStatus: (buildingId, approvalStatus, filters = {}) => 
    api.get('/expenses', { params: { buildingId, approvalStatus, ...filters } }),
  
  // Get expenses by urgency level
  getByUrgency: (buildingId, urgencyLevel, filters = {}) => 
    api.get('/expenses', { params: { buildingId, urgencyLevel, ...filters } }),
  
  // Get expenses within amount range
  getByAmountRange: (buildingId, minAmount, maxAmount, filters = {}) => 
    api.get('/expenses', { params: { buildingId, minAmount, maxAmount, ...filters } }),
  
  // Generate expense report (if implemented)
  generateReport: (buildingId, reportType, params = {}) => 
    api.get('/expenses/reports', { params: { buildingId, reportType, ...params } }),
  
  // Get budget analysis (if implemented)
  getBudgetAnalysis: (buildingId, month) => 
    api.get('/expenses/budget-analysis', { params: { buildingId, month } })
};

// Utility function for making API calls with proper error handling
export const apiCall = async (endpoint, method = 'GET', data = null, config = {}) => {
  try {
    const response = await api({
      url: endpoint,
      method,
      data,
      ...config
    });
    
    return {
      success: true,
      data: response.data,
      status: response.status
    };
  } catch (error) {
    console.error(`API call failed: ${method} ${endpoint}`, error);
    
    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status || 500
    };
  }
};

export default api;
